import { useState, useEffect } from "react";
import { X, BellPlus, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function fmtPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * NotifyBackModal — small modal customers see when they tap a sold-out item.
 * They enter their phone number and we add them to a per-item waitlist. The
 * truck owner reads the list from the admin panel and texts them personally
 * when the item comes back (no SMS integration required).
 */
export default function NotifyBackModal({ item, onClose }) {
  const [phone, setPhone] = useState(
    () =>
      (typeof window !== "undefined"
        ? localStorage.getItem("bk_loyalty_phone")
        : "") || ""
  );
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [othersWaiting, setOthersWaiting] = useState(null);

  // Fetch how many others are already waiting so the customer knows they're not the first.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/menu/${item.id}/restock-waitlist/count`);
        if (!cancelled) setOthersWaiting(data?.count ?? 0);
      } catch {
        /* non-critical — the modal still works without this */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.id]);

  const digits = phone.replace(/\D/g, "");
  const valid = digits.length >= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    try {
      await api.post(`/menu/${item.id}/restock-waitlist`, { phone: digits });
      localStorage.setItem("bk_loyalty_phone", digits);
      setDone(true);
      toast.success("We'll text you when it's back!");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't sign up. Try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="notify-back-title"
      data-testid="notify-back-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-float-in"
      >
        <div className="chalkboard-band px-4 py-3 flex items-center justify-between border-b-4 border-brand-red">
          <div className="flex items-center gap-2">
            <BellPlus className="w-5 h-5 text-brand-yellow" />
            <div
              id="notify-back-title"
              className="font-display text-brand-yellow text-lg"
            >
              Notify me when back
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="notify-back-close"
            aria-label="Close"
            className="text-white/70 hover:text-white p-1 -mr-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!done ? (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <div className="font-hand text-3xl text-stone-800 leading-tight">
                {item.name}
              </div>
              <p className="text-sm text-stone-500 mt-1">
                Sold out today — but we&apos;ll text you the moment it&apos;s back on
                the truck. No spam, promise.
              </p>
              {othersWaiting !== null && othersWaiting > 0 && (
                <p className="text-[11px] text-brand-red font-bold mt-2 uppercase tracking-widest">
                  🔔 {othersWaiting}{" "}
                  {othersWaiting === 1 ? "person is" : "people are"} already
                  waiting on this
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="notify-back-phone"
                className="text-[10px] uppercase tracking-widest text-stone-500 font-black"
              >
                Your phone
              </label>
              <input
                id="notify-back-phone"
                type="tel"
                inputMode="tel"
                value={fmtPhone(phone)}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                placeholder="(479) 555-1234"
                autoFocus
                data-testid="notify-back-phone-input"
                className="w-full mt-1 px-4 py-3 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-base font-mono tracking-wide"
              />
            </div>

            <button
              type="submit"
              disabled={!valid || saving}
              data-testid="notify-back-submit"
              className="w-full bg-brand-red text-white py-3 rounded-full font-black uppercase tracking-widest text-sm hover:bg-red-800 disabled:opacity-40 active:scale-[0.98] transition-all"
            >
              {saving ? "Adding you..." : "Text Me When It's Back"}
            </button>
            <p className="text-[10px] text-stone-400 text-center leading-tight">
              The truck owner will text you personally when the item returns.
              You can reply STOP any time.
            </p>
          </form>
        ) : (
          <div
            className="p-8 text-center space-y-3"
            data-testid="notify-back-success"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="font-hand text-3xl text-stone-800">
              You&apos;re on the list!
            </div>
            <p className="text-sm text-stone-500">
              We&apos;ll text{" "}
              <span className="font-mono font-bold text-stone-800">
                {fmtPhone(phone)}
              </span>{" "}
              the second {item.name} is back on the truck.
            </p>
            <button
              onClick={onClose}
              className="mt-2 bg-stone-900 text-white px-6 py-2.5 rounded-full font-bold uppercase text-xs tracking-widest"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
